/**
 * AudioPlayerContext - Global Audio Player for Device App
 * 
 * Features:
 * - Persistent audio playback across navigation
 * - Radio streaming (MP3/AAC/HLS)
 * - Integration with TTS and Voice Control
 * - Pause/Resume management for priority audio (TTS, calls)
 * 
 * Usage:
 * ```jsx
 * const { play, pause, isPlaying, currentStation } = useAudioPlayer();
 * 
 * // Play radio
 * play(radioStation);
 * 
 * // Pause for TTS
 * pause();
 * ```
 */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';

const AudioPlayerContext = createContext(null);

export function AudioPlayerProvider({ children }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStation, setCurrentStation] = useState(null);
  const [volume, setVolume] = useState(0.7);
  const [isPaused, setIsPaused] = useState(false); // For pause/resume management
  
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const retryCount = useRef(0);
  const maxRetries = 3;
  const wasPlayingBeforePause = useRef(false); // Track state for resume

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;

    // Load saved volume
    const savedVolume = localStorage.getItem('hayat_radio_volume');
    if (savedVolume) {
      const vol = parseFloat(savedVolume);
      setVolume(vol);
      audioRef.current.volume = vol;
    }

    // Load last played station
    const savedStation = localStorage.getItem('hayat_radio_last_station');
    if (savedStation) {
      try {
        const station = JSON.parse(savedStation);
        setCurrentStation(station);
        console.log('[AudioPlayer] Restored last station:', station.name);
      } catch (e) {
        console.error('[AudioPlayer] Failed to parse saved station:', e);
      }
    }

    // Audio event listeners
    const audio = audioRef.current;

    const handlePlay = () => {
      console.log('[AudioPlayer] Playback started');
      setIsPlaying(true);
      setIsLoading(false);
      setError(null);
      setIsPaused(false);
      retryCount.current = 0;
    };

    const handlePause = () => {
      console.log('[AudioPlayer] Playback paused');
      setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error('[AudioPlayer] Playback error:', e);
      console.error('[AudioPlayer] Failed station:', {
        name: currentStation?.name,
        url: currentStation?.url,
        format: currentStation?.format
      });
      setIsLoading(false);
      
      // Try fallback URL if available
      if (currentStation?.fallbackUrl && retryCount.current < maxRetries) {
        retryCount.current++;
        console.log(`[AudioPlayer] Trying fallback URL (attempt ${retryCount.current})`);
        const fallbackFormat = currentStation.fallbackFormat || currentStation.format || 'mp3';
        loadStream(currentStation.fallbackUrl, fallbackFormat);
      } else {
        setIsPlaying(false);
        const errorMsg = `Sender "${currentStation?.name || 'Unbekannt'}" kann nicht abgespielt werden.`;
        setError(errorMsg);
        console.error('[AudioPlayer] Final error:', errorMsg);
      }
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      // Don't pause on unmount - keep audio playing!
    };
  }, []);

  // Load stream (MP3/AAC or HLS) - iPad Safari optimized
  const loadStream = (url, format = 'mp3') => {
    if (!audioRef.current) return;

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Determine if HLS stream
    const isHLS = format === 'hls' || url.includes('.m3u8') || url.includes('.m3u');
    
    if (isHLS) {
      // iPad/Safari: Try native HLS first
      if (audioRef.current.canPlayType('application/vnd.apple.mpegurl') || 
          audioRef.current.canPlayType('application/x-mpegURL')) {
        console.log('[AudioPlayer] Using native HLS support (iPad/Safari)');
        audioRef.current.src = url;
        audioRef.current.load(); // Important for iOS
        audioRef.current.play().catch(e => {
          console.error('[AudioPlayer] Native HLS play error:', e);
          setError('Wiedergabe fehlgeschlagen');
          setIsPlaying(false);
          setIsLoading(false);
        });
      } 
      // Desktop: Use hls.js
      else if (Hls.isSupported()) {
        console.log('[AudioPlayer] Using hls.js for HLS stream');
        const hls = new Hls({
          maxLoadingDelay: 10,
          maxBufferLength: 30,
          enableWorker: true,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 10
        });
        
        hls.loadSource(url);
        hls.attachMedia(audioRef.current);
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('[AudioPlayer] HLS manifest parsed');
          audioRef.current.play().catch(e => {
            console.error('[AudioPlayer] HLS play error:', e);
            setError('Wiedergabe fehlgeschlagen');
          });
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('[AudioPlayer] HLS error:', data);
          if (data.fatal) {
            setError('Stream konnte nicht geladen werden');
            setIsPlaying(false);
            setIsLoading(false);
          }
        });

        hlsRef.current = hls;
      } else {
        setError('HLS-Streams werden auf diesem Gerät nicht unterstützt');
        setIsPlaying(false);
        setIsLoading(false);
      }
    } else {
      // Standard MP3/AAC stream
      console.log('[AudioPlayer] Loading MP3/AAC stream:', url);
      audioRef.current.src = url;
      audioRef.current.load(); // Important for iOS
      audioRef.current.play().catch(e => {
        console.error('[AudioPlayer] MP3/AAC play error:', e);
        setError('Wiedergabe fehlgeschlagen');
        setIsPlaying(false);
        setIsLoading(false);
      });
    }
  };

  // Play station
  const play = (station) => {
    if (!station || !station.url) {
      console.error('[AudioPlayer] Invalid station data');
      return;
    }

    console.log('[AudioPlayer] Playing station:', station.name, 'format:', station.format);
    setCurrentStation(station);
    setError(null);
    retryCount.current = 0;
    
    // Save to localStorage
    localStorage.setItem('hayat_radio_last_station', JSON.stringify(station));
    
    // Load and play with format hint
    loadStream(station.url, station.format || 'mp3');
  };

  // Pause playback
  const pause = () => {
    if (audioRef.current && isPlaying) {
      console.log('[AudioPlayer] Pausing playback');
      wasPlayingBeforePause.current = true;
      audioRef.current.pause();
      setIsPaused(true);
    }
  };

  // Resume playback
  const resume = () => {
    if (audioRef.current && isPaused && currentStation) {
      console.log('[AudioPlayer] Resuming playback');
      audioRef.current.play().catch(e => {
        console.error('[AudioPlayer] Resume error:', e);
      });
      wasPlayingBeforePause.current = false;
      setIsPaused(false);
    }
  };

  // Stop (pause + reset)
  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentStation(null);
    setIsPaused(false);
    wasPlayingBeforePause.current = false;
    localStorage.removeItem('hayat_radio_last_station');
  };

  // Change volume
  const changeVolume = (newVolume) => {
    const vol = Math.max(0, Math.min(1, newVolume));
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
    localStorage.setItem('hayat_radio_volume', vol.toString());
  };

  // Next station in current category
  // Note: stations must be passed from MusicView via currentStation.categoryStations
  const next = () => {
    if (!currentStation || !currentStation.categoryId || !currentStation.categoryStations) {
      console.warn('[AudioPlayer] Cannot go to next: missing category data');
      return;
    }

    const stations = currentStation.categoryStations;
    const currentIndex = stations.findIndex(s => s.id === currentStation.id);
    const nextIndex = (currentIndex + 1) % stations.length;
    const nextStation = { 
      ...stations[nextIndex], 
      categoryId: currentStation.categoryId,
      categoryStations: stations
    };
    
    console.log('[AudioPlayer] Next station:', nextStation.name);
    play(nextStation);
  };

  // Previous station in current category
  const previous = () => {
    if (!currentStation || !currentStation.categoryId || !currentStation.categoryStations) {
      console.warn('[AudioPlayer] Cannot go to previous: missing category data');
      return;
    }

    const stations = currentStation.categoryStations;
    const currentIndex = stations.findIndex(s => s.id === currentStation.id);
    const prevIndex = currentIndex === 0 ? stations.length - 1 : currentIndex - 1;
    const prevStation = { 
      ...stations[prevIndex], 
      categoryId: currentStation.categoryId,
      categoryStations: stations
    };
    
    console.log('[AudioPlayer] Previous station:', prevStation.name);
    play(prevStation);
  };

  // Get audio element for external control (e.g., voice commands)
  const getAudioElement = () => audioRef.current;

  const value = {
    // State
    isPlaying,
    isLoading,
    error,
    currentStation,
    volume,
    isPaused,
    
    // Actions
    play,
    pause,
    resume,
    stop,
    next,
    previous,
    changeVolume,
    getAudioElement
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

// Hook to use audio player
export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
}
